import { FC, createContext, useContext, useState, useCallback } from 'react';

import { useCookies } from '~/shared/hooks/useCookies';
import { cookies } from '~/shared/constants/cookies';

import { ILoadTicketsProsp } from './tickets';
import { TicketScheduledPeriodOptions } from '../enums/TicketScheduledPeriodOptions';
import { OrderStatusEnum } from '../enums/OrderStatusEnum';
import { HeaderOptionsEnum } from '../enums/HeaderOptionsEnum';

interface IHeaderContextData {
  selectedInHeader: HeaderOptionsEnum;
  scheduledPeriod: TicketScheduledPeriodOptions;
  handleDisplaySwitch: (statusJourney: HeaderOptionsEnum[]) => void;
  changeSelectedInHeader: (
    newSelectedInHeader: HeaderOptionsEnum,
    callback?: (data: ILoadTicketsProsp) => void
  ) => void;
  changeScheduledPeriod: (
    newScheduledPeriod: TicketScheduledPeriodOptions
  ) => void;
  getPreviousStatusToReturn: () => OrderStatusEnum;
  oldSelectedInHeader: HeaderOptionsEnum;
}

const HeaderContext = createContext({} as IHeaderContextData);

const HeaderProvider: FC = ({ children }) => {
  const cookiesManager = useCookies();
  const selectedInHeaderDefaultValue = (): HeaderOptionsEnum => {
    const storagedSelectedInHeader = cookiesManager.getItem(
      cookies.SELECTED_IN_HEADER
    );

    if (storagedSelectedInHeader) {
      return storagedSelectedInHeader as HeaderOptionsEnum;
    }

    return HeaderOptionsEnum.open;
  };

  const [oldSelectedInHeader, setOldSelectedInHeader] =
    useState<HeaderOptionsEnum>(selectedInHeaderDefaultValue);
  const [selectedInHeader, setSelectedInHeader] = useState<HeaderOptionsEnum>(
    selectedInHeaderDefaultValue
  );
  const [scheduledPeriod, setScheduledPeriod] =
    useState<TicketScheduledPeriodOptions>(TicketScheduledPeriodOptions.Today);

  const getPreviousStatusToReturn = useCallback(() => {
    switch (selectedInHeader) {
      case HeaderOptionsEnum.delivered:
        return OrderStatusEnum.done;
      default:
        return OrderStatusEnum.open;
    }
  }, [selectedInHeader]);

  const changeSelectedInHeader = useCallback(
    (
      newSelectedInHeader: HeaderOptionsEnum,
      callback?: (data: ILoadTicketsProsp) => void
    ) => {
      if (selectedInHeader !== newSelectedInHeader) {
        setOldSelectedInHeader(selectedInHeader);
        setSelectedInHeader((prev) =>
          prev === newSelectedInHeader ? null : newSelectedInHeader
        );

        if (selectedInHeader !== newSelectedInHeader) {
          const nextScheduledPeriod =
            newSelectedInHeader === HeaderOptionsEnum.scheduled
              ? TicketScheduledPeriodOptions.Today
              : scheduledPeriod;

          if (newSelectedInHeader === HeaderOptionsEnum.scheduled) {
            setScheduledPeriod(TicketScheduledPeriodOptions.Today);
          }

          cookiesManager.setItem(
            cookies.SELECTED_IN_HEADER,
            newSelectedInHeader
          );

          if (callback)
            callback({
              selectedInHeader: newSelectedInHeader,
              scheduledPeriod: nextScheduledPeriod,
              reset: true,
              withLoad: true,
              page: 1,
            });
        } else {
          cookiesManager.setItem(cookies.SELECTED_IN_HEADER, null);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduledPeriod, selectedInHeader]
  );

  const changeScheduledPeriod = useCallback(
    (newScheduledPeriod: TicketScheduledPeriodOptions) => {
      setScheduledPeriod(newScheduledPeriod);
    },
    []
  );

  const handleDisplaySwitch = useCallback(
    (statusJourney: HeaderOptionsEnum[]) => {
      if (
        selectedInHeader !== HeaderOptionsEnum.scheduled &&
        !(statusJourney || []).includes(selectedInHeader)
      ) {
        setSelectedInHeader(statusJourney?.[0] || HeaderOptionsEnum.open);
      }
    },
    [selectedInHeader]
  );

  return (
    <HeaderContext.Provider
      value={{
        selectedInHeader,
        scheduledPeriod,
        oldSelectedInHeader,
        changeSelectedInHeader,
        changeScheduledPeriod,
        handleDisplaySwitch,
        getPreviousStatusToReturn,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
};

const useHeader = (): IHeaderContextData => {
  return useContext(HeaderContext);
};

export { HeaderProvider, useHeader };
